import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchPinnedStudents,
  togglePinnedStudent,
} from "../api/pinned-student.api";
import { useAuth } from "@/features/auth";

export const usePinnedStudents = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["pinned-students", user?._id];

  const pinnedStudentsQuery = useQuery({
    queryKey,
    queryFn: () => fetchPinnedStudents(),
    enabled: !!user?._id,
  });

  const toggleMutation = useMutation({
    mutationFn: (email) => togglePinnedStudent(email),
    onMutate: async (email) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old = []) => {
        const exists = old.some((p) => p.student?.email === email);

        if (exists) {
          return old.filter((p) => p.student?.email !== email);
        }

        return [
          {
            _id: crypto.randomUUID(),
            student: { email },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          ...old,
        ];
      });

      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    pinnedStudents: pinnedStudentsQuery.data || [],
    isLoading: pinnedStudentsQuery.isLoading,
    error: pinnedStudentsQuery.error,
    togglePinnedStudent: toggleMutation.mutateAsync,
    isPinning: toggleMutation.isPending,
  };
};
